import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { TourismMapCategory, TourismMapItemResponse, TourismMapRequest, TourismMapTranslation } from './tourism-map.model';
import { TourismMapService } from './tourism-map.service';

interface TourismMapListRow {
  tourismMapItemId: number;
  image: string;
  category: TourismMapCategory;
  markerLeft: number;
  markerTop: number;
  sortOrder: number;
  isPublished: boolean;
  languageCode: string;
  languageId: number;
  name: string;
  province: string;
  region: string;
}

@Component({
  selector: 'app-tourism-map-admin',
  standalone: false,
  templateUrl: './tourism-map.component.html',
  styleUrl: './tourism-map.component.css'
})
export class TourismMapComponent implements OnInit {
  successMessage = '';
  errorMessage = '';

  languages: any[] = [];
  items: TourismMapListRow[] = [];
  filteredItems: TourismMapListRow[] = [];

  currentPage = 1;
  pageSize = 8;
  activeTab = 'kh';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;
  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  tourismMapItemId = 0;
  image: File | null = null;
  imagePreview = '';
  category: TourismMapCategory = 'Heritage';
  markerLeft = 43;
  markerTop = 43;
  sortOrder = 1;
  isPublished = true;

  nameKh = '';
  provinceKh = '';
  regionKh = '';
  descriptionKh = '';
  bestSeasonKh = '';
  travelTimeKh = '';
  visitorFocusKh = '';
  highlightsKh = '';

  nameEn = '';
  provinceEn = '';
  regionEn = '';
  descriptionEn = '';
  bestSeasonEn = '';
  travelTimeEn = '';
  visitorFocusEn = '';
  highlightsEn = '';

  readonly categories: Array<{ value: TourismMapCategory; label: string }> = [
    { value: 'Heritage', label: 'Heritage' },
    { value: 'Coastal', label: 'Coastal' },
    { value: 'Nature', label: 'Nature' },
    { value: 'City', label: 'City' }
  ];

  constructor(
    private tourismMapService: TourismMapService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadItems();
  }

  loadLanguages(): void {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = (res.data || []).sort((a: any, b: any) => a.languageId - b.languageId);

      if (this.languages.length > 0) {
        this.activeTab = this.languages[0].code;
        this.activeTabPopup = this.languages[0].languageId;
      }
    });
  }

  loadItems(): void {
    this.tourismMapService.getAll().subscribe({
      next: (res: any) => {
        const apiData: TourismMapItemResponse[] = res.data || [];

        this.items = apiData.flatMap(item =>
          (item.translations || []).map((translation: TourismMapTranslation) => ({
            tourismMapItemId: item.tourismMapItemId,
            image: item.image,
            category: item.category,
            markerLeft: item.markerLeft,
            markerTop: item.markerTop,
            sortOrder: item.sortOrder,
            isPublished: item.isPublished,
            languageCode: translation.languageCode || '',
            languageId: translation.languageId,
            name: translation.name,
            province: translation.province,
            region: translation.region
          }))
        );

        this.filterItems(this.activeTab);
      },
      error: () => this.showError('Failed to load tourism map items.')
    });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    this.filterItems(tab);
  }

  setTabPopup(languageId: number): void {
    this.activeTabPopup = languageId;
  }

  filterItems(tab: string): void {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredItems = this.items.filter(item => {
      const matchesTab = item.languageCode === tab;
      const matchesSearch = !keyword ||
        `${item.name} ${item.province} ${item.region} ${item.category}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  get pagedItems(): TourismMapListRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page;
  }

  getLanguageCount(lang: string): number {
    return this.items.filter(item => item.languageCode === lang).length;
  }

  addNew(): void {
    this.visibleButtonControl('Add');
    this.resetForm();
    this.openModal();
  }

  viewData(id: number, eventName: string): void {
    this.visibleButtonControl(eventName);
    this.tourismMapItemId = id;

    this.tourismMapService.getById(id).subscribe({
      next: (res: any) => {
        if (res.status !== 'success') {
          this.showError(res.message || 'Failed to load tourism map item.');
          return;
        }

        const data = res.data;
        this.tourismMapItemId = data.tourismMapItemId;
        this.category = data.category || 'Heritage';
        this.markerLeft = Number(data.markerLeft ?? 0);
        this.markerTop = Number(data.markerTop ?? 0);
        this.sortOrder = Number(data.sortOrder ?? 0);
        this.isPublished = Boolean(data.isPublished);
        this.imagePreview = data.image || '';
        this.image = null;

        this.clearTranslationFields();

        (data.translations || []).forEach((translation: TourismMapTranslation) => {
          if (translation.languageCode === 'kh') {
            this.nameKh = translation.name;
            this.provinceKh = translation.province;
            this.regionKh = translation.region;
            this.descriptionKh = translation.description;
            this.bestSeasonKh = translation.bestSeason;
            this.travelTimeKh = translation.travelTime;
            this.visitorFocusKh = translation.visitorFocus;
            this.highlightsKh = (translation.highlights || []).join(', ');
          }

          if (translation.languageCode === 'en') {
            this.nameEn = translation.name;
            this.provinceEn = translation.province;
            this.regionEn = translation.region;
            this.descriptionEn = translation.description;
            this.bestSeasonEn = translation.bestSeason;
            this.travelTimeEn = translation.travelTime;
            this.visitorFocusEn = translation.visitorFocus;
            this.highlightsEn = (translation.highlights || []).join(', ');
          }
        });

        this.openModal();
      },
      error: () => this.showError('Failed to load tourism map item.')
    });
  }

  save(): void {
    this.clearMsg();

    this.tourismMapService.create(this.getRequest()).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: () => this.showError('Failed to create tourism map item.')
    });
  }

  update(): void {
    this.clearMsg();

    this.tourismMapService.update(this.tourismMapItemId, this.getRequest(this.tourismMapItemId)).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: () => this.showError('Failed to update tourism map item.')
    });
  }

  delete(): void {
    if (!confirm('Are you sure you want to delete this tourism map item?')) {
      return;
    }

    this.clearMsg();

    this.tourismMapService.delete(this.tourismMapItemId).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: () => this.showError('Failed to delete tourism map item.')
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.image = file;
    this.imagePreview = URL.createObjectURL(file);
  }

  openModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  visibleButtonControl(eventName: string): void {
    this.isEnableSave = eventName === 'Add';
    this.isEnableUpdate = eventName === 'Edit';
    this.isEnableDelete = eventName === 'Delete';
  }

  resetForm(): void {
    this.tourismMapItemId = 0;
    this.image = null;
    this.imagePreview = '';
    this.category = 'Heritage';
    this.markerLeft = 43;
    this.markerTop = 43;
    this.sortOrder = this.items.length + 1;
    this.isPublished = true;
    this.clearTranslationFields();
  }

  private getRequest(tourismMapItemId?: number): TourismMapRequest {
    return {
      image: this.image || undefined,
      data: {
        tourismMapItemId,
        category: this.category,
        markerLeft: Number(this.markerLeft) || 0,
        markerTop: Number(this.markerTop) || 0,
        sortOrder: Number(this.sortOrder) || 0,
        isPublished: this.isPublished,
        translations: [
          {
            languageId: this.getLanguageId('kh', 1),
            name: this.nameKh,
            province: this.provinceKh,
            region: this.regionKh,
            description: this.descriptionKh,
            bestSeason: this.bestSeasonKh,
            travelTime: this.travelTimeKh,
            visitorFocus: this.visitorFocusKh,
            highlights: this.toHighlightList(this.highlightsKh)
          },
          {
            languageId: this.getLanguageId('en', 2),
            name: this.nameEn,
            province: this.provinceEn,
            region: this.regionEn,
            description: this.descriptionEn,
            bestSeason: this.bestSeasonEn,
            travelTime: this.travelTimeEn,
            visitorFocus: this.visitorFocusEn,
            highlights: this.toHighlightList(this.highlightsEn)
          }
        ]
      }
    };
  }

  private getLanguageId(code: string, fallbackId: number): number {
    return this.languages.find(language => language.code === code)?.languageId ?? fallbackId;
  }

  private toHighlightList(value: string): string[] {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  private clearTranslationFields(): void {
    this.nameKh = '';
    this.provinceKh = '';
    this.regionKh = '';
    this.descriptionKh = '';
    this.bestSeasonKh = '';
    this.travelTimeKh = '';
    this.visitorFocusKh = '';
    this.highlightsKh = '';

    this.nameEn = '';
    this.provinceEn = '';
    this.regionEn = '';
    this.descriptionEn = '';
    this.bestSeasonEn = '';
    this.travelTimeEn = '';
    this.visitorFocusEn = '';
    this.highlightsEn = '';
  }

  private refreshData(status: string, message: string): void {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadItems();
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    this.clearMsgLater();
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.clearMsgLater();
  }

  private clearMsg(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  private clearMsgLater(): void {
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }
}
